import { Link, useNavigate } from 'react-router-dom';
import { Media } from '../ui/Media';
import { Icon } from '../ui/Icon';
import { Bouton, Compteur, Segmente, Vide } from '../ui/kit';
import { BlocPaiement, Fil, clsBouton } from './partages';
import { useBoutique } from '../state/boutique';
import { arrhes, eur, fraisLivraison, kg, manquePourLivraisonOfferte } from '../data/repository';
import { BOUTIQUES, REGLAGES } from '../data/mock-data';

export function Panier() {
  const { lignes, nbArticles, sousTotal, mode, setMode, boutiqueId, setBoutique, majQuantite, majPoids, retirer, vider } = useBoutique();
  const nav = useNavigate();
  const frais = fraisLivraison(sousTotal, mode);
  const total = Math.round((sousTotal + frais) * 100) / 100;
  const manque = manquePourLivraisonOfferte(sousTotal);
  const ar = arrhes(total, mode);
  const boutique = BOUTIQUES.find(b => b.id === boutiqueId) ?? BOUTIQUES[0];

  if (nbArticles === 0) {
    return (
      <main id="contenu" className="mx-auto max-w-3xl px-5 py-12">
        <Fil fils={[{ libelle: 'Panier' }]} />
        <Vide icone="cart" titre="Votre panier est vide"
          texte="Rien n’est réservé pour l’instant. Commencez par la côte de bœuf — c’est la pièce qu’on nous demande le plus."
          action={<Link to="/boutique" className={clsBouton('primary')}>Découvrir le comptoir</Link>} />
      </main>
    );
  }

  return (
    <main id="contenu" className="mx-auto max-w-[1180px] px-5 py-8 sm:px-6">
      <Fil fils={[{ libelle: 'Boutique', vers: '/boutique' }, { libelle: 'Panier' }]} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-[clamp(1.9rem,4vw,2.8rem)]">Votre panier</h1>
        <button type="button" onClick={vider} className="text-sm text-muted underline underline-offset-4 hover:text-danger">
          Vider le panier
        </button>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px] lg:gap-12">
        {/* ------------------------------------------------------------ lignes */}
        <div>
          <ul className="divide-y divide-linen-deep/60">
            {lignes.map(l => (
              <li key={l.cle} className="flex gap-4 py-5 first:pt-0">
                <Link to={`/produit/${l.produit.slug}`} className="shrink-0">
                  <Media src={l.produit.vignette} alt={l.produit.nom} ratio="aspect-square"
                    className="h-24 w-24 rounded-2xl sm:h-28 sm:w-28" monogramme={l.produit.nom.slice(0, 1)} />
                </Link>

                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="eyebrow">{l.produit.familleNom}</p>
                      <Link to={`/produit/${l.produit.slug}`} className="mt-0.5 block truncate font-display text-lg leading-snug hover:text-wine">
                        {l.produit.nom}
                      </Link>
                      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem] text-muted">
                        <span className="rounded-full bg-linen px-2 py-0.5">
                          {l.produit.modele === 'AU_KG' ? kg(l.poidsG) : l.produit.modele === 'FORMATS' ? `format ${kg(l.poidsG)}` : 'à la pièce'}
                        </span>
                        {Object.entries(l.options).map(([k, v]) => (
                          <span key={k} className="rounded-full bg-linen px-2 py-0.5">{v}</span>
                        ))}
                      </p>
                      {l.produit.modele === 'AU_KG' && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-[0.75rem] text-muted">Poids</span>
                          <div className="inline-flex items-center rounded-full bg-linen">
                            <button type="button" onClick={() => majPoids(l.cle, Math.max(l.produit.portion.minG, l.poidsG - (l.produit.portion.pasG || 50)))}
                              disabled={l.poidsG <= l.produit.portion.minG}
                              className="grid h-11 w-11 place-items-center rounded-full text-ink-soft transition hover:bg-surface disabled:opacity-30"
                              aria-label={`Diminuer le poids de ${l.produit.nom}`}>
                              <Icon name="minus" size={14} />
                            </button>
                            <span className="min-w-14 text-center text-[0.8125rem] font-semibold tabular-nums">{kg(l.poidsG)}</span>
                            <button type="button" onClick={() => majPoids(l.cle, Math.min(l.produit.portion.maxG, l.poidsG + (l.produit.portion.pasG || 50)))}
                              disabled={l.poidsG >= l.produit.portion.maxG}
                              className="grid h-11 w-11 place-items-center rounded-full text-ink-soft transition hover:bg-surface disabled:opacity-30"
                              aria-label={`Augmenter le poids de ${l.produit.nom}`}>
                              <Icon name="plus" size={14} />
                            </button>
                          </div>
                          <span className="text-[0.6875rem] text-muted">± {REGLAGES.poids.tolerancePct} % ajusté après pesée</span>
                        </div>
                      )}
                      {l.note && (
                        <p className="mt-1.5 rounded-lg bg-brass-wash/60 px-2.5 py-1.5 text-[0.75rem] text-brass-ink">
                          <strong className="font-semibold">Note au boucher :</strong> {l.note}
                        </p>
                      )}
                    </div>
                    <button type="button" onClick={() => retirer(l.cle)}
                      className="shrink-0 rounded-full p-2 text-muted transition hover:bg-linen hover:text-danger"
                      aria-label={`Retirer ${l.produit.nom}`}>
                      <Icon name="trash" size={17} />
                    </button>
                  </div>

                  <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-3">
                    <Compteur valeur={l.quantite} onChange={q => majQuantite(l.cle, q)} libelle={l.produit.nom} taille="sm" />
                    <div className="text-right">
                      <p className="font-semibold tabular-nums">{eur(l.total)}</p>
                      {l.quantite > 1 && <p className="text-xs text-muted">{eur(l.prixUnitaire)} l’unité</p>}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <Link to="/boutique" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold underline underline-offset-4">
            <Icon name="chevronLeft" size={16} /> Continuer mes achats
          </Link>
        </div>

        {/* ---------------------------------------------------------- récapitulatif */}
        <aside className="lg:sticky lg:top-[120px] lg:self-start">
          <div className="rounded-[22px] bg-surface p-5 shadow-card sm:p-6">
            <h2 className="font-display text-xl">Récapitulatif</h2>

            <div className="mt-4">
              <p className="mb-2 text-sm font-semibold">Comment souhaitez-vous être servi ?</p>
              <Segmente bloc libelle="Mode de service" valeur={mode}
                onChange={v => setMode(v as 'PICKUP' | 'DELIVERY')}
                options={[{ valeur: 'PICKUP', libelle: 'Retrait', icone: 'store' }, { valeur: 'DELIVERY', libelle: 'Livraison', icone: 'truck' }]} />

              {mode === 'PICKUP' ? (
                <label className="mt-3 block">
                  <span className="mb-1.5 block text-xs font-medium text-muted">Boutique de retrait</span>
                  <select value={boutiqueId} onChange={e => setBoutique(e.target.value)} className="select h-12">
                    {BOUTIQUES.map(b => <option key={b.id} value={b.id}>{b.nom} — {b.adresse}</option>)}
                  </select>
                </label>
              ) : (
                <p className="mt-3 rounded-xl bg-linen px-3 py-2.5 text-[0.8125rem] text-ink-soft">
                  Colis {REGLAGES.livraison.temperature} · {REGLAGES.livraison.transporteur}, {REGLAGES.livraison.delaiMinH}–{REGLAGES.livraison.delaiMaxH} h ouvrées.
                </p>
              )}
            </div>

            {mode === 'DELIVERY' && (
              <div className="mt-4">
                <div className="mb-1.5 flex justify-between text-[0.8125rem]">
                  <span className="text-ink-soft">{manque > 0 ? `Plus que ${eur(manque)}` : 'Livraison offerte'}</span>
                  <span className="text-muted">seuil {eur(REGLAGES.livraison.seuilGratuite)}</span>
                </div>
                <div className="meter"><div className="meter__fill" style={{ width: `${Math.min(100, (sousTotal / REGLAGES.livraison.seuilGratuite) * 100)}%` }} /></div>
              </div>
            )}

            <dl className="mt-5 space-y-2 border-t border-linen-deep/70 pt-4 text-sm">
              <div className="flex justify-between"><dt className="text-ink-soft">Sous-total</dt><dd className="tabular-nums">{eur(sousTotal)}</dd></div>
              <div className="flex justify-between">
                <dt className="text-ink-soft">{mode === 'PICKUP' ? `Retrait — ${boutique.nom}` : 'Livraison réfrigérée'}</dt>
                <dd className="tabular-nums">{frais === 0 ? 'Offert' : eur(frais)}</dd>
              </div>
              <div className="flex justify-between border-t border-linen-deep/70 pt-2.5 text-base font-semibold">
                <dt>Total</dt><dd className="tabular-nums">{eur(total)}</dd>
              </div>
            </dl>

            <div className="mt-4">
              <BlocPaiement total={total} ar={ar} />
            </div>

            <Bouton bloc taille="lg" className="mt-5" onClick={() => nav('/commande')}>
              Passer commande <Icon name="arrowRight" size={18} />
            </Bouton>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted">
              <Icon name="shield" size={14} /> Paiement sécurisé — démonstration, aucune carte n’est débitée.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
}
